import { useCallback, useRef, useState } from "react";
import {
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Image,
  Text,
  VStack,
  useColorModeValue,
  usePrefersReducedMotion,
} from "@chakra-ui/react";
import { ChevronDownIcon } from "@chakra-ui/icons";
import { keyframes } from "@emotion/react";
import { useTranslation } from "react-i18next";
import Login from "./Login";
import capabilityUploadSvg from "../assets/document-intro/capability-upload.svg";
import capabilityLivePreviewSvg from "../assets/document-intro/capability-live-preview.svg";
import stepFileUploadSvg from "../assets/document-intro/undraw_collaborative-writing_ir40.svg";
import stepProcessingSvg from "../assets/document-intro/processing.svg";
import stepRevealSvg from "../assets/document-intro/reveal.svg";
import stepInteractionSvg from "../assets/document-intro/interaction.svg";
import heroTextFilesSvg from "../assets/document-intro/undraw_text-files_tqjw.svg";

const slideIn = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to { opacity: 1; transform: translateY(0); }
`;

const shimmer = keyframes`
  0% { transform: translateX(-40%); opacity: 0; }
  40% { opacity: 0.6; }
  100% { transform: translateX(140%); opacity: 0; }
`;

const uiDrift = keyframes`
  0% { transform: scale(1) translateY(0); }
  50% { transform: scale(1.015) translateY(-3px); }
  100% { transform: scale(1) translateY(0); }
`;

const slowBounce = keyframes`
  0% { transform: translateY(0); }
  50% { transform: translateY(-5px); }
  100% { transform: translateY(0); }
`;

const CapabilityCard = ({
  idPrefix,
  imageDataUri,
  title,
  body,
  cardBg,
  cardBorder,
  delay = "0s",
}: {
  idPrefix: string;
  imageDataUri: string;
  title: string;
  body: string;
  cardBg: string;
  cardBorder: string;
  delay?: string;
}) => {
  const prefersReducedMotion = usePrefersReducedMotion();

  return (
    <Box
      id={`${idPrefix}-card`}
      w="100%"
      maxW={{ base: "2xl", md: "none" }}
      flex={{ base: "0 0 100%", md: "0 0 auto" }}
      flexShrink={0}
      p={5}
      borderRadius="md"
      borderWidth="1px"
      borderColor={cardBorder}
      bg={cardBg}
      boxShadow="sm"
      scrollSnapAlign="start"
      animation={
        prefersReducedMotion ? undefined : `${slideIn} 0.75s ease ${delay} both`
      }
    >
      <VStack id={`${idPrefix}-card-stack`} spacing={4} align="center">
        <Box
          id={`${idPrefix}-image-wrap`}
          position="relative"
          w="100%"
          borderRadius="xl"
          overflow="hidden"
          borderWidth="0"
          borderColor="transparent"
        >
          <Image
            id={`${idPrefix}-image`}
            src={imageDataUri}
            alt={title}
            w="100%"
            h="auto"
            display="block"
            animation={
              prefersReducedMotion
                ? undefined
                : `${uiDrift} 6s ease-in-out ${delay} infinite`
            }
          />
          <Box
            id={`${idPrefix}-image-shimmer`}
            position="absolute"
            top={0}
            left={0}
            bottom={0}
            width="32%"
            bgGradient="linear(to-r, transparent, whiteAlpha.300, transparent)"
            pointerEvents="none"
            animation={
              prefersReducedMotion
                ? undefined
                : `${shimmer} 3.2s ease-in-out ${delay} infinite`
            }
          />
        </Box>
        <VStack
          id={`${idPrefix}-card-content`}
          align="stretch"
          spacing={2}
          textAlign="left"
          w="100%"
        >
          <Heading id={`${idPrefix}-card-title`} size="sm">
            {title}
          </Heading>
          <Text
            id={`${idPrefix}-card-body`}
            fontSize="sm"
            color={useColorModeValue("gray.600", "gray.300")}
          >
            {body}
          </Text>
        </VStack>
      </VStack>
    </Box>
  );
};

const DocumentIntro = () => {
  const { t } = useTranslation();
  const prefersReducedMotion = usePrefersReducedMotion();
  const capabilitiesGridRef = useRef<HTMLDivElement | null>(null);
  const walkthroughRailRef = useRef<HTMLDivElement | null>(null);
  const walkthroughStepRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [activeCapabilityIndex, setActiveCapabilityIndex] = useState(0);
  const [activeWalkthroughStepId, setActiveWalkthroughStepId] =
    useState("file-upload");
  const panelBg = useColorModeValue(
    "linear(to-b, white, gray.50)",
    "linear(to-b, gray.800, gray.900)",
  );
  const pageBg = useColorModeValue("gray.50", "gray.800");
  const softText = useColorModeValue("gray.600", "gray.300");
  const cardBg = useColorModeValue("white", "gray.800");
  const cardBorder = useColorModeValue("gray.200", "gray.600");
  const capabilityCount = 2;

  const capabilities = [
    {
      idPrefix: "document-intro-capability-1",
      imageDataUri: capabilityUploadSvg,
      title: t("document.introCapability1Title", "Multi-format upload"),
      body: t(
        "document.introCapability1Body",
        "Drop PDF, DOCX, PPTX, XLSX, TXT, CSV, HTML and more into one converter.",
      ),
      delay: "0s",
    },
    {
      idPrefix: "document-intro-capability-2",
      imageDataUri: capabilityLivePreviewSvg,
      title: t("document.introCapability2Title", "Live preview + edit"),
      body: t(
        "document.introCapability2Body",
        "Review source and translation side-by-side, then edit before export.",
      ),
      delay: "0.08s",
    },
  ];

  const walkthroughSteps = [
    {
      id: "file-upload",
      title: t(
        "document.introWalkthroughUploadProcessing",
        "1. Upload + processing",
      ),
      description: t(
        "document.introWalkthroughUploadProcessingDescription",
        "Upload your document, choose target language, and start processing.",
      ),
      imageSrc: stepFileUploadSvg,
    },
    {
      id: "reveal",
      title: t("document.introWalkthroughReveal", "2. Reveal"),
      description: t(
        "document.introWalkthroughRevealDescription",
        "See source and translated result side by side.",
      ),
      imageSrc: stepRevealSvg,
    },
    {
      id: "interaction",
      title: t("document.introWalkthroughInteraction", "3. Interaction"),
      description: t(
        "document.introWalkthroughInteractionDescription",
        "Review, edit, and export your final output.",
      ),
      imageSrc: stepInteractionSvg,
    },
  ];

  const updateActiveWalkthroughStep = useCallback(() => {
    const rail = walkthroughRailRef.current;
    if (!rail) return;
    const railRect = rail.getBoundingClientRect();
    const railCenter = railRect.left + railRect.width / 2;

    let closestId = walkthroughSteps[0]?.id;
    let closestDistance = Number.POSITIVE_INFINITY;

    walkthroughSteps.forEach((step) => {
      const el = walkthroughStepRefs.current[step.id];
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const center = rect.left + rect.width / 2;
      const distance = Math.abs(center - railCenter);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestId = step.id;
      }
    });

    if (closestId) setActiveWalkthroughStepId(closestId);
  }, [walkthroughSteps]);

  const updateActiveCapability = useCallback(() => {
    const grid = capabilitiesGridRef.current;
    if (!grid) return;
    const pageWidth = Math.max(grid.clientWidth, 1);
    const index = Math.round(grid.scrollLeft / pageWidth);
    const clampedIndex = Math.max(0, Math.min(capabilityCount - 1, index));
    setActiveCapabilityIndex(clampedIndex);
  }, [capabilityCount]);

  return (
    <VStack id="document-intro" align="stretch" spacing={6}>
      <Box
        h={{ base: "auto", md: "78vh" }}
        overflowY={{ base: "visible", md: "auto" }}
        overflowX="hidden"
        borderRadius="2xl"
        scrollSnapType={{ base: "none", md: "y mandatory" }}
        bgGradient={panelBg}
        boxShadow="xl"
        sx={{
          "@media (min-width: 48em)": {
            scrollbarWidth: "none",
            // Allow scroll chaining to the page body; without this, once the
            // inner panel reaches its end, wheel/trackpad scrolling won't
            // reach the sibling login section.
            overscrollBehaviorY: "auto",
            "::-webkit-scrollbar": { display: "none" },
          },
        }}
      >
        <VStack id="document-intro-sections" align="stretch" spacing={0}>
          <Flex
            id="document-intro-hero"
            direction="column"
            minH={{ base: "82dvh", md: "unset" }}
            h={{ base: "auto", md: "78vh" }}
            boxSizing="border-box"
            flexShrink={0}
            px={{ base: 5, md: 10 }}
            py={{ base: 8, md: 10 }}
            scrollSnapAlign={{ base: "none", md: "start" }}
            scrollSnapStop={{ base: "normal", md: "always" }}
          >
            <VStack
              spacing={4}
              justify="center"
              textAlign="center"
              // Fill available height so the text can actually be vertically centered.
              flex="1"
              w="100%"
            >
              <Image
                id="document-intro-hero-illustration"
                src={heroTextFilesSvg}
                alt=""
                aria-hidden="true"
                w="100%"
                maxW={{ base: "260px", md: "360px" }}
                h="auto"
                mx="auto"
                animation={
                  prefersReducedMotion
                    ? undefined
                    : `${uiDrift} 7s ease-in-out infinite`
                }
              />
              <Heading
                id="document-intro-hero-title"
                size={{ base: "lg", md: "xl" }}
              >
                {t(
                  "document.introHeroTitle",
                  "Turn any document into readable, translated content",
                )}
              </Heading>
              <Text
                id="document-intro-hero-body"
                maxW="2xl"
                mx="auto"
                color={softText}
                fontSize={{ base: "md", md: "lg" }}
              >
                {t(
                  "document.introHeroBody",
                  "Upload PDFs, Office files, and text docs. Get clean preview, translation, and downloadable output in one flow.",
                )}
              </Text>
            </VStack>
            <Flex
              flex="1"
              minH={0}
              align={{ base: "center", md: "flex-end" }}
              justify="center"
              w="100%"
              pt={{ base: 4, md: 6 }}
            >
              <HStack
                id="document-intro-hero-scroll-hint"
                color={softText}
                spacing={1}
                justify="center"
                w="100%"
                animation={
                  prefersReducedMotion
                    ? undefined
                    : `${slowBounce} 2.6s ease-in-out infinite`
                }
              >
                <Text id="document-intro-hero-scroll-hint-text" fontSize="sm">
                  {t("document.introScrollHint", "Scroll to continue")}
                </Text>
                <ChevronDownIcon id="document-intro-hero-scroll-hint-icon" />
              </HStack>
            </Flex>
          </Flex>

          <VStack
            id="document-intro-capabilities"
            minH={{ base: "82dvh", md: "unset" }}
            h={{ base: "auto", md: "78vh" }}
            boxSizing="border-box"
            flexShrink={0}
            px={{ base: 5, md: 10 }}
            pb={{ base: 8, md: 10 }}
            pt={{ base: 8, md: 10 }}
            spacing={0}
            justify={{ base: "flex-start", md: "flex-start" }}
            overflowY="visible"
            scrollSnapAlign={{ base: "none", md: "start" }}
            scrollSnapStop={{ base: "normal", md: "always" }}
            align="stretch"
            sx={{
              scrollbarWidth: "none",
              "::-webkit-scrollbar": { display: "none" },
            }}
          >
            <VStack
              flex="1"
              justify="center"
              align="stretch"
              spacing={4}
              minH={0}
            >
              <Heading
                id="document-intro-capabilities-title"
                size={{ base: "lg", md: "xl" }}
                fontWeight="semibold"
                color={softText}
                textAlign="center"
              >
                {t("document.introCapabilitiesTitle", "What you can do")}
              </Heading>
              <VStack
                id="document-intro-capabilities-grid"
                ref={capabilitiesGridRef}
                w="100%"
                maxW={{ base: "3xl", md: "none" }}
                spacing={4}
                maxH={{ base: "none", md: "50vh" }}
                overflowX={{ base: "auto", md: "visible" }}
                overflowY={{ base: "hidden", md: "auto" }}
                flexDirection={{ base: "row", md: "column" }}
                align={{ base: "center", md: "stretch" }}
                onScroll={updateActiveCapability}
                sx={{
                  scrollbarWidth: "none",
                  "::-webkit-scrollbar": { display: "none" },
                  scrollSnapType: "x mandatory",
                }}
              >
                {capabilities.map((capability) => (
                  <CapabilityCard
                    key={capability.idPrefix}
                    idPrefix={capability.idPrefix}
                    imageDataUri={capability.imageDataUri}
                    title={capability.title}
                    body={capability.body}
                    cardBg={cardBg}
                    cardBorder={cardBorder}
                    delay={capability.delay}
                  />
                ))}
              </VStack>
              <HStack
                id="document-intro-capabilities-progress"
                justify="center"
                spacing={2}
                pt={1}
                display={{ base: "flex", md: "none" }}
              >
                {Array.from({ length: capabilityCount }).map((_, index) => {
                  const isActive = activeCapabilityIndex === index;
                  return (
                    <Box
                      key={`document-intro-capabilities-progress-${index + 1}`}
                      id={`document-intro-capabilities-progress-${index + 1}`}
                      w={isActive ? "18px" : "8px"}
                      h="8px"
                      borderRadius="full"
                      bg={isActive ? "blue.400" : "whiteAlpha.500"}
                      transition="all 0.2s ease"
                    />
                  );
                })}
              </HStack>
            </VStack>
          </VStack>

          <VStack
            id="document-intro-steps"
            minH={{ base: "82dvh", md: "unset" }}
            h={{ base: "auto", md: "78vh" }}
            boxSizing="border-box"
            flexShrink={0}
            px={{ base: 5, md: 10 }}
            py={{ base: 8, md: 10 }}
            spacing={0}
            justify="flex-start"
            scrollSnapAlign={{ base: "none", md: "start" }}
            scrollSnapStop={{ base: "normal", md: "always" }}
            align="stretch"
          >
            <VStack
              flex="1"
              justify="center"
              align="stretch"
              spacing={4}
              minH={0}
            >
              <Heading
                id="document-intro-steps-title"
                size={{ base: "lg", md: "xl" }}
                fontWeight="semibold"
                color={softText}
                textAlign="center"
              >
                {t("document.introStepsTitle", "How it works in 3 steps")}
              </Heading>
              <Flex
                direction="column"
                align="stretch"
                justify="center"
                minH={0}
                py={{ base: 4, md: 0 }}
              >
                <Box
                  id="document-intro-cta-walkthrough"
                  w="100%"
                  maxW={{ base: "2xl", md: "none" }}
                  p={{ base: 3, md: 4 }}
                  borderRadius="2xl"
                  borderWidth="0"
                  bg="transparent"
                  boxShadow="none"
                  overflow="hidden"
                  position="relative"
                >
                  <VStack
                    id="document-intro-cta-walkthrough-stack"
                    spacing={3}
                    align="stretch"
                  >
                    <HStack
                      id="document-intro-cta-walkthrough-rail"
                      ref={walkthroughRailRef}
                      spacing={{ base: 0, md: 3 }}
                      overflowX="auto"
                      overflowY="hidden"
                      align="stretch"
                      onScroll={updateActiveWalkthroughStep}
                      sx={{
                        scrollbarWidth: "none",
                        "::-webkit-scrollbar": { display: "none" },
                        scrollSnapType: "x mandatory",
                      }}
                    >
                      {walkthroughSteps.map((step, index) => (
                        <VStack
                          id={`document-intro-cta-walkthrough-step-${step.id}`}
                          key={step.id}
                          ref={(el) => {
                            walkthroughStepRefs.current[step.id] = el;
                          }}
                          w={{ base: "100%", md: "72%" }}
                          minW={{ base: "100%", md: "72%" }}
                          flex={{ base: "0 0 100%", md: "0 0 72%" }}
                          boxSizing="border-box"
                          spacing={2}
                          align="stretch"
                          p={5}
                          borderRadius="md"
                          borderWidth="1px"
                          borderColor={cardBorder}
                          bg={cardBg}
                          boxShadow="sm"
                          scrollSnapAlign="start"
                          flexShrink={0}
                          transform={
                            activeWalkthroughStepId === step.id
                              ? "scale(1)"
                              : "scale(0.92)"
                          }
                          opacity={
                            activeWalkthroughStepId === step.id ? 1 : 0.72
                          }
                          transition="transform 0.25s ease, opacity 0.25s ease"
                        >
                          <Text
                            id={`document-intro-cta-walkthrough-step-${step.id}-label`}
                            textAlign="center"
                            fontWeight="semibold"
                          >
                            {step.title}
                          </Text>
                          <Text
                            id={`document-intro-cta-walkthrough-step-${step.id}-description`}
                            textAlign="center"
                            fontSize="sm"
                            color={softText}
                          >
                            {step.description}
                          </Text>
                          <Box
                            id={`document-intro-cta-walkthrough-step-${step.id}-image-wrap`}
                            borderRadius="lg"
                            overflow="hidden"
                            borderWidth="0"
                            borderColor="transparent"
                          >
                            <Image
                              id={`document-intro-cta-walkthrough-step-${step.id}-image`}
                              src={step.imageSrc}
                              alt={step.title}
                              w="100%"
                              maxH={{ base: "180px", md: "230px" }}
                              h="auto"
                              display="block"
                              objectFit="cover"
                              animation={
                                prefersReducedMotion
                                  ? undefined
                                  : `${uiDrift} 5.6s ease-in-out ${index * 0.08}s infinite`
                              }
                            />
                          </Box>
                        </VStack>
                      ))}
                    </HStack>
                    <HStack
                      id="document-intro-cta-walkthrough-progress"
                      justify="center"
                      spacing={2}
                      pt={1}
                    >
                      {walkthroughSteps.map((step) => {
                        const isActive = activeWalkthroughStepId === step.id;
                        return (
                          <Box
                            key={`document-intro-cta-walkthrough-progress-${step.id}`}
                            id={`document-intro-cta-walkthrough-progress-${step.id}`}
                            w={isActive ? "18px" : "8px"}
                            h="8px"
                            borderRadius="full"
                            bg={isActive ? "blue.400" : "whiteAlpha.500"}
                            transition="all 0.2s ease"
                          />
                        );
                      })}
                    </HStack>
                  </VStack>
                </Box>
              </Flex>
            </VStack>
          </VStack>

          <VStack
            id="document-intro-login"
            align="stretch"
            spacing={0}
            bg={pageBg}
            minH={{ base: "82dvh", md: "unset" }}
            h={{ base: "auto", md: "78vh" }}
            boxSizing="border-box"
            flexShrink={0}
            px={{ base: 5, md: 10 }}
            py={{ base: 8, md: 10 }}
            justify="center"
            scrollSnapAlign={{ base: "none", md: "start" }}
            scrollSnapStop={{ base: "normal", md: "always" }}
          >
            <Box id="document-intro-login-content" w="100%">
              <Login redirectTo="/document" />
            </Box>
          </VStack>
        </VStack>
      </Box>
    </VStack>
  );
};

export default DocumentIntro;
